import { Building2, Pencil, Plus, Search, ShieldCheck, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/features/auth';
import {
  useAdminClubsQuery,
  useCreateClubMutation,
  useDeleteClubMutation,
  useUpdateClubMutation,
  type AdminClub,
  type UpdateClubRequest,
} from '@/features/club';
import { fullNameOf, useUserSearchQuery, type UserProfile } from '@/features/user';
import { ApiError } from '@/shared/api/ApiError';
import {
  Alert,
  Avatar,
  Badge,
  Button,
  Checkbox,
  EmptyState,
  FormField,
  IconButton,
  Input,
  Modal,
  PageHeader,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableHeaderCell,
  TableRow,
  useToast,
} from '@/shared/ui';

/** Pola adresowo-opisowe wspolne dla tworzenia i edycji klubu. */
interface ClubFormState {
  name: string;
  description: string;
  city: string;
  street: string;
  postalCode: string;
  country: string;
}

const EMPTY_FORM: ClubFormState = {
  name: '',
  description: '',
  city: '',
  street: '',
  postalCode: '',
  country: 'Polska',
};

/** Zamienia pusty string na null — API woli brak wartosci niz pusty tekst. */
function orNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

/** Buduje payload edycji/tworzenia z pol formularza. */
function toRequest(form: ClubFormState): UpdateClubRequest {
  return {
    name: form.name.trim(),
    description: orNull(form.description),
    logoUrl: null,
    city: orNull(form.city),
    street: orNull(form.street),
    postalCode: orNull(form.postalCode),
    country: orNull(form.country),
    latitude: null,
    longitude: null,
  };
}

/**
 * Panel zarzadzania klubami — wylacznie dla roli ADMIN.
 *
 * Podstawowy CRUD: lista, tworzenie (z wyborem wlasciciela), edycja danych
 * podstawowych i trwale usuwanie. Twarda kontrola dostepu siedzi w backendzie
 * (POST/PUT/DELETE /api/clubs wymagaja roli ADMIN).
 */
export function ClubsAdminPage() {
  const { t } = useTranslation('common');
  const { hasRole } = useAuth();
  const { showToast } = useToast();
  const isAdmin = hasRole('ADMIN');

  const clubsQuery = useAdminClubsQuery(isAdmin);
  const createMutation = useCreateClubMutation();
  const updateMutation = useUpdateClubMutation();
  const deleteMutation = useDeleteClubMutation();

  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<AdminClub | null>(null);
  const [deleting, setDeleting] = useState<AdminClub | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);

  const [form, setForm] = useState<ClubFormState>(EMPTY_FORM);
  const [virtualClub, setVirtualClub] = useState(false);

  // Wybor wlasciciela nowego klubu — proste autouzupelnianie po bazie graczy.
  const [ownerQuery, setOwnerQuery] = useState('');
  const [owner, setOwner] = useState<UserProfile | null>(null);
  const ownerSearch = useUserSearchQuery(ownerQuery);

  const clubs = useMemo(() => clubsQuery.data ?? [], [clubsQuery.data]);
  const visible = useMemo(() => {
    const phrase = search.trim().toLowerCase();
    if (!phrase) return clubs;
    return clubs.filter((club) =>
      `${club.name} ${club.city ?? ''}`.toLowerCase().includes(phrase),
    );
  }, [clubs, search]);

  function describe(error: unknown, fallback: string): string {
    return error instanceof ApiError ? error.message : fallback;
  }

  function openCreate() {
    setDialogError(null);
    setForm(EMPTY_FORM);
    setVirtualClub(false);
    setOwner(null);
    setOwnerQuery('');
    setCreateOpen(true);
  }

  function openEdit(club: AdminClub) {
    setDialogError(null);
    setForm({
      name: club.name,
      description: club.description ?? '',
      city: club.city ?? '',
      street: club.street ?? '',
      postalCode: club.postalCode ?? '',
      country: club.country ?? '',
    });
    setEditing(club);
  }

  function handleCreate() {
    if (!owner) return;
    setDialogError(null);
    createMutation.mutate(
      { ...toRequest(form), ownerId: owner.id, virtualClub },
      {
        onSuccess: () => {
          setCreateOpen(false);
          showToast({ tone: 'success', description: t('clubsAdmin.toast.created') });
        },
        onError: (error) => setDialogError(describe(error, t('clubsAdmin.toast.createFailed'))),
      },
    );
  }

  function handleUpdate() {
    if (!editing) return;
    setDialogError(null);
    updateMutation.mutate(
      { clubId: editing.id, request: toRequest(form) },
      {
        onSuccess: () => {
          setEditing(null);
          showToast({ tone: 'success', description: t('clubsAdmin.toast.updated') });
        },
        onError: (error) => setDialogError(describe(error, t('clubsAdmin.toast.updateFailed'))),
      },
    );
  }

  function handleDelete() {
    if (!deleting) return;
    deleteMutation.mutate(deleting.id, {
      onSuccess: () => {
        setDeleting(null);
        showToast({ tone: 'success', description: t('clubsAdmin.toast.deleted') });
      },
      onError: (error) => {
        setDeleting(null);
        showToast({ tone: 'danger', description: describe(error, t('clubsAdmin.toast.deleteFailed')) });
      },
    });
  }

  /** Wspolne pola formularza klubu — dialogi tworzenia i edycji roznia sie tylko naglowkiem. */
  const formFields = (
    <>
      <FormField label={t('clubsAdmin.form.name')} required>
        <Input
          value={form.name}
          onChange={(event) => setForm({ ...form, name: event.target.value })}
        />
      </FormField>
      <FormField label={t('clubsAdmin.form.description')}>
        <Input
          value={form.description}
          onChange={(event) => setForm({ ...form, description: event.target.value })}
        />
      </FormField>
      <div className="grid grid-cols-2 gap-3">
        <FormField label={t('clubsAdmin.form.city')}>
          <Input
            value={form.city}
            onChange={(event) => setForm({ ...form, city: event.target.value })}
          />
        </FormField>
        <FormField label={t('clubsAdmin.form.street')}>
          <Input
            value={form.street}
            onChange={(event) => setForm({ ...form, street: event.target.value })}
          />
        </FormField>
        <FormField label={t('clubsAdmin.form.postalCode')}>
          <Input
            value={form.postalCode}
            onChange={(event) => setForm({ ...form, postalCode: event.target.value })}
          />
        </FormField>
        <FormField label={t('clubsAdmin.form.country')}>
          <Input
            value={form.country}
            onChange={(event) => setForm({ ...form, country: event.target.value })}
          />
        </FormField>
      </div>
    </>
  );

  const header = (
    <PageHeader title={t('clubsAdmin.title')} description={t('clubsAdmin.description')} />
  );

  if (!isAdmin) {
    return (
      <>
        {header}
        <EmptyState
          icon={<ShieldCheck />}
          title={t('clubsAdmin.forbiddenTitle')}
          description={t('clubsAdmin.forbiddenDescription')}
          className="min-h-[16rem]"
        />
      </>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      {header}

      <div className="flex flex-wrap items-center gap-2">
        <Button leadingIcon={<Plus aria-hidden="true" />} onClick={openCreate}>
          {t('clubsAdmin.add')}
        </Button>

        <Input
          inputSize="sm"
          type="search"
          aria-label={t('clubsAdmin.search')}
          placeholder={t('clubsAdmin.search')}
          leadingIcon={<Search />}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className="w-72"
        />
      </div>

      {clubsQuery.isError && <Alert tone="danger">{t('clubsAdmin.loadError')}</Alert>}
      {clubsQuery.isPending && <Skeleton className="h-64 w-full" />}

      {clubsQuery.isSuccess && clubs.length === 0 && (
        <EmptyState
          icon={<Building2 />}
          title={t('clubsAdmin.emptyTitle')}
          description={t('clubsAdmin.emptyDescription')}
          className="min-h-[16rem]"
        />
      )}

      {clubsQuery.isSuccess && clubs.length > 0 && visible.length === 0 && (
        <EmptyState
          icon={<Search />}
          title={t('clubsAdmin.noMatchTitle')}
          description={t('clubsAdmin.noMatchDescription')}
          className="min-h-[16rem]"
        />
      )}

      {visible.length > 0 && (
        <TableContainer>
          <Table className="[&_td]:px-3 [&_th]:px-3">
            <TableHead>
              <TableRow>
                <TableHeaderCell className="w-12">{t('clubsAdmin.columns.index')}</TableHeaderCell>
                <TableHeaderCell>{t('clubsAdmin.columns.club')}</TableHeaderCell>
                <TableHeaderCell>{t('clubsAdmin.columns.address')}</TableHeaderCell>
                <TableHeaderCell className="w-28">{t('clubsAdmin.columns.type')}</TableHeaderCell>
                <TableHeaderCell className="w-24 text-right">
                  {t('clubsAdmin.columns.facilities')}
                </TableHeaderCell>
                <TableHeaderCell className="w-24 text-right">
                  {t('clubsAdmin.columns.actions')}
                </TableHeaderCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {visible.map((club, index) => (
                <TableRow key={club.id}>
                  <TableCell className="text-fg-subtle text-sm tabular-nums">
                    {index + 1}
                  </TableCell>

                  <TableCell>
                    <div className="flex items-center gap-2.5">
                      <Avatar src={club.logoUrl} name={club.name} size="sm" fit="contain" />
                      <div className="min-w-0">
                        <p className="text-fg-strong truncate text-sm font-semibold">{club.name}</p>
                        {club.description && (
                          <p className="text-fg-muted truncate text-xs">{club.description}</p>
                        )}
                      </div>
                    </div>
                  </TableCell>

                  <TableCell className="text-fg text-sm">
                    {[club.street, club.city].filter(Boolean).join(', ') || '—'}
                  </TableCell>

                  <TableCell>
                    <Badge tone={club.clubType === 'VIRTUAL' ? 'warning' : 'neutral'} size="sm">
                      {club.clubType === 'VIRTUAL'
                        ? t('clubsAdmin.type.virtual')
                        : t('clubsAdmin.type.standard')}
                    </Badge>
                  </TableCell>

                  <TableCell className="text-fg text-right text-sm tabular-nums">
                    {club.facilities.length}
                  </TableCell>

                  <TableCell>
                    <div className="flex justify-end gap-1">
                      <IconButton
                        variant="secondary"
                        size="sm"
                        icon={<Pencil />}
                        label={t('clubsAdmin.actions.edit')}
                        onClick={() => openEdit(club)}
                      />
                      <IconButton
                        variant="danger"
                        size="sm"
                        icon={<Trash2 />}
                        label={t('clubsAdmin.actions.delete')}
                        onClick={() => setDeleting(club)}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <TableFooter>
            {t('clubsAdmin.footer', { shown: visible.length, total: clubs.length })}
          </TableFooter>
        </TableContainer>
      )}

      {/* Nowy klub */}
      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        size="md"
        title={t('clubsAdmin.createDialog.title')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setCreateOpen(false)}>
              {t('clubsAdmin.createDialog.cancel')}
            </Button>
            <Button
              loading={createMutation.isPending}
              disabled={!form.name.trim() || !owner}
              onClick={handleCreate}
            >
              {t('clubsAdmin.createDialog.submit')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          {dialogError && <Alert tone="danger">{dialogError}</Alert>}
          {formFields}

          <FormField
            label={t('clubsAdmin.createDialog.owner')}
            hint={t('clubsAdmin.createDialog.ownerHint')}
            required
          >
            {owner ? (
              <div className="border-border bg-surface-raised flex items-center justify-between gap-2 rounded-lg border px-3 py-2">
                <span className="flex items-center gap-2.5">
                  <Avatar name={fullNameOf(owner)} size="sm" colorful />
                  <span className="flex min-w-0 flex-col">
                    <span className="text-fg-strong truncate text-sm font-semibold">
                      {fullNameOf(owner)}
                    </span>
                    <span className="text-fg-muted truncate text-xs">{owner.email}</span>
                  </span>
                </span>
                <Button variant="secondary" size="sm" onClick={() => setOwner(null)}>
                  {t('clubsAdmin.createDialog.changeOwner')}
                </Button>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <Input
                  type="search"
                  placeholder={t('clubsAdmin.createDialog.ownerSearch')}
                  leadingIcon={<Search />}
                  value={ownerQuery}
                  onChange={(event) => setOwnerQuery(event.target.value)}
                />
                {(ownerSearch.data ?? []).slice(0, 5).map((candidate) => (
                  <button
                    key={candidate.id}
                    type="button"
                    onClick={() => setOwner(candidate)}
                    className="border-border bg-surface hover:bg-surface-hover flex items-center gap-2.5 rounded-lg border px-3 py-2 text-left"
                  >
                    <Avatar name={fullNameOf(candidate)} size="sm" colorful />
                    <span className="flex min-w-0 flex-col">
                      <span className="text-fg-strong truncate text-sm font-semibold">
                        {fullNameOf(candidate)}
                      </span>
                      <span className="text-fg-muted truncate text-xs">{candidate.email}</span>
                    </span>
                  </button>
                ))}
              </div>
            )}
          </FormField>

          <Checkbox
            label={t('clubsAdmin.createDialog.virtual')}
            description={t('clubsAdmin.createDialog.virtualHint')}
            checked={virtualClub}
            onChange={(event) => setVirtualClub(event.target.checked)}
          />
        </div>
      </Modal>

      {/* Edycja klubu */}
      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        size="md"
        title={t('clubsAdmin.editDialog.title')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setEditing(null)}>
              {t('clubsAdmin.editDialog.cancel')}
            </Button>
            <Button
              loading={updateMutation.isPending}
              disabled={!form.name.trim()}
              onClick={handleUpdate}
            >
              {t('clubsAdmin.editDialog.submit')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          {dialogError && <Alert tone="danger">{dialogError}</Alert>}
          {formFields}
        </div>
      </Modal>

      {/* Potwierdzenie usuniecia */}
      <Modal
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        size="sm"
        title={t('clubsAdmin.confirmDelete.title')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeleting(null)}>
              {t('clubsAdmin.confirmDelete.keep')}
            </Button>
            <Button variant="danger" loading={deleteMutation.isPending} onClick={handleDelete}>
              {t('clubsAdmin.confirmDelete.confirm')}
            </Button>
          </>
        }
      >
        {deleting && t('clubsAdmin.confirmDelete.body', { name: deleting.name })}
      </Modal>
    </div>
  );
}
